import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  Hash,
  Type,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Database,
  Loader2,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import dataProfileService from '../services/dataProfileService';
import clientAIService from '../services/clientAIService';
import ChartSuggestions from './ChartSuggestions';
import exportService from '../services/exportService';

const DataProfile = ({ selectedFile, onQuestionClick }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const loadProfileData = useCallback(async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const profileData = await dataProfileService.generateProfile(selectedFile.file_id);
      setProfile(profileData);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (selectedFile) {
      loadProfileData();
    } else {
      setProfile(null);
      setAiInsights(null);
    }
  }, [selectedFile, loadProfileData]);

  const loadAIInsights = async () => {
    if (!profile) return;

    setInsightsLoading(true);
    try {
      const insights = await clientAIService.generateDataInsights(
        profile.tableName,
        profile.columns,
        null,
        { totalRows: profile.totalRows, columnStats: profile.columnStats }
      );
      setAiInsights(insights);
    } catch (error) {
      console.error('Error generating AI insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  if (!selectedFile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Database className="w-12 h-12 text-sand-500 mx-auto mb-3" />
          <p className="text-sand-600">Select a file to view data profile</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-bill-500 animate-spin mx-auto mb-3" />
          <p className="text-sand-600">Analyzing your data...</p>
        </div>
      </div>
    );
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'numeric': return <Hash className="w-4 h-4 text-bill-600" />;
      case 'categorical': return <Type className="w-4 h-4 text-sage-600" />;
      case 'date': return <Calendar className="w-4 h-4 text-bill-700" />;
      default: return <Type className="w-4 h-4 text-sand-500" />;
    }
  };

  const getQualityColor = (score) => {
    if (score >= 80) return 'text-sage-600';
    if (score >= 60) return 'text-bill-600';
    return 'text-red-600';
  };

  const schemaRows = profile?.columns?.map((column) => {
    const stats = profile.columnStats?.[column] || {};
    const nullPct = Number(stats.nullPercentage || 0);
    return {
      name: column,
      dbType: stats.dbType || 'unknown',
      nullable: stats.nullable,
      nullCount: stats.nullCount || 0,
      nullPercentage: Number.isNaN(nullPct) ? 0 : nullPct,
      uniqueCount: stats.uniqueCount || 0
    };
  }) || [];

  const handleExportReport = (format) => {
    if (!profile) return;
    const filename = `duckpilot_report_${selectedFile?.filename || 'dataset'}`
      .replace(/\s+/g, '_')
      .toLowerCase();

    const schemaSection = {
      title: 'Schema',
      description: 'Column types and null metrics.',
      data: schemaRows.map((row) => ({
        column: row.name,
        type: row.dbType,
        nullable: row.nullable === null ? 'Unknown' : row.nullable ? 'Yes' : 'No',
        nulls: `${row.nullCount} (${row.nullPercentage}%)`,
        unique: row.uniqueCount
      }))
    };

    const missingSection = {
      title: 'Missing Values',
      description: 'Columns with missing data.',
      data: (profile.missingValues || []).map((item) => ({
        column: item.column,
        nulls: `${item.nullCount} (${item.nullPercentage}%)`
      }))
    };

    const outlierSection = {
      title: 'Outlier Signals',
      description: 'Numeric columns with outlier indicators.',
      data: (profile.outliers || []).map((item) => ({
        column: item.column,
        outliers: `${item.outlierCount} (${item.outlierPercentage}%)`
      }))
    };

    const suggestionsSection = {
      title: 'Suggested Questions',
      description: 'Quick questions to explore the dataset.',
      data: (profile.suggestions || []).map((item) => ({
        question: item.question,
        description: item.description
      }))
    };

    const sections = [schemaSection, missingSection, outlierSection, suggestionsSection];

    if (format === 'json') {
      exportService.exportReport(sections, filename);
      return;
    }

    exportService.exportReportHTML({
      title: `DuckPilot Report: ${selectedFile?.filename || 'Dataset'}`,
      summary: `Rows: ${profile.totalRows?.toLocaleString() || 0} • Columns: ${profile.columns?.length || 0}`,
      sections,
      filename
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-ink">Data Profile</h2>
          <p className="text-sm text-sand-600">Export a shareable report for this dataset.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleExportReport('html')}
            className="px-3 py-2 rounded-lg bg-white border border-[rgba(32,30,29,0.18)] text-ink hover:bg-sand-100 text-sm transition-colors"
          >
            Export HTML
          </button>
          <button
            type="button"
            onClick={() => handleExportReport('json')}
            className="px-3 py-2 rounded-lg bg-white border border-[rgba(32,30,29,0.18)] text-ink hover:bg-sand-100 text-sm transition-colors"
          >
            Export JSON
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {profile && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-bill-600" />
              <span className="text-sm text-sand-700">Total Rows</span>
            </div>
            <p className="text-2xl font-bold text-ink tnum">{profile.totalRows?.toLocaleString()}</p>
          </div>

          <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-bill-600" />
              <span className="text-sm text-sand-700">Columns</span>
            </div>
            <p className="text-2xl font-bold text-ink tnum">
              {profile.columns?.length}
              {profile.columnLimitApplied && (
                <span className="text-xs text-sand-600 ml-2">of {profile.totalColumns}</span>
              )}
            </p>
          </div>

          <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-sage-600" />
              <span className="text-sm text-sand-700">Completeness</span>
            </div>
            <p className="text-2xl font-bold text-ink tnum">{profile.dataQuality?.completeness}%</p>
          </div>

          <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className={`w-4 h-4 ${getQualityColor(profile.dataQuality?.score)}`} />
              <span className="text-sm text-sand-700">Quality Score</span>
            </div>
            <p className={`text-2xl font-bold tnum ${getQualityColor(profile.dataQuality?.score)}`}>
              {profile.dataQuality?.score?.toFixed(0)}
            </p>
          </div>
        </div>
      )}

      {profile?.sampled && (
        <div className="bg-sand-100 border border-[rgba(32,30,29,0.08)] rounded-brand-md p-4 text-sm text-sand-700">
          Showing sampled profiling for performance. Stats are based on the first {profile.sampleSize?.toLocaleString()} rows.
        </div>
      )}

      {/* Data Quality Issues */}
      {profile?.dataQuality?.issues?.length > 0 && (
        <div className="bg-bill-100 border border-bill-200 rounded-brand-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-bill-600" />
            <h3 className="font-medium text-bill-800">Data Quality Issues</h3>
          </div>
          <ul className="space-y-2">
            {profile.dataQuality.issues.map((issue, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-sand-700">
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  issue.severity === 'high' ? 'bg-red-100 text-red-700' :
                  issue.severity === 'medium' ? 'bg-bill-200 text-bill-800' :
                  'bg-sand-200 text-sand-700'
                }`}>
                  {issue.severity}
                </span>
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Missing Values & Outliers */}
      {(profile?.missingValues?.length > 0 || profile?.outliers?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {profile?.missingValues?.length > 0 && (
            <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
              <h3 className="font-medium text-ink mb-4">Top Missing Values</h3>
              <div className="space-y-3">
                {profile.missingValues.map((item) => (
                  <div key={item.column} className="flex items-center justify-between text-sm">
                    <span className="text-sand-800">{item.column}</span>
                    <span className="text-sand-600 tnum">
                      {item.nullCount.toLocaleString()} ({item.nullPercentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile?.outliers?.length > 0 && (
            <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
              <h3 className="font-medium text-ink mb-4">Outlier Signals</h3>
              <div className="space-y-3">
                {profile.outliers.map((item) => (
                  <div key={item.column} className="flex items-center justify-between text-sm">
                    <span className="text-sand-800">{item.column}</span>
                    <span className="text-sand-600 tnum">
                      {item.outlierCount.toLocaleString()} ({item.outlierPercentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schema Explorer */}
      {schemaRows.length > 0 && (
        <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-ink">Schema Explorer</h3>
            <button
              onClick={loadProfileData}
              className="p-1.5 hover:bg-sand-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-sand-600" />
            </button>
          </div>

          <div className="overflow-x-auto rounded-brand-sm border border-[rgba(32,30,29,0.12)]">
            <table className="w-full text-sm">
              <thead className="bg-sand-100 border-b border-[rgba(32,30,29,0.08)]">
                <tr className="text-left text-sand-600 uppercase tracking-wide">
                  <th className="px-3 py-2 font-medium">Column</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Nullable</th>
                  <th className="px-3 py-2 font-medium">Nulls</th>
                  <th className="px-3 py-2 font-medium">Unique</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand-200">
                {schemaRows.map((row) => (
                  <tr key={row.name} className="text-sand-800 hover:bg-sand-100 transition-colors">
                    <td className="px-3 py-2 font-medium text-ink">{row.name}</td>
                    <td className="px-3 py-2">{row.dbType}</td>
                    <td className="px-3 py-2">
                      {row.nullable === null ? 'Unknown' : (row.nullable ? 'Yes' : 'No')}
                    </td>
                    <td className="px-3 py-2 tnum">
                      {row.nullCount.toLocaleString()} ({row.nullPercentage}%)
                    </td>
                    <td className="px-3 py-2 tnum">
                      {row.uniqueCount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {profile && (
        <ChartSuggestions
          profile={profile}
          selectedFile={selectedFile}
        />
      )}

      {/* Column Statistics */}
      {profile?.columnStats && (
        <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-ink">Column Statistics</h3>
            <button
              onClick={loadProfileData}
              className="p-1.5 hover:bg-sand-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-sand-600" />
            </button>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
            {Object.entries(profile.columnStats).map(([column, stats]) => (
              <div key={column} className="p-3 bg-sand-100 rounded-brand-sm border border-[rgba(32,30,29,0.08)]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(stats.type)}
                    <span className="font-medium text-ink">{column}</span>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-sand-200 rounded text-sand-700">
                    {stats.type}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-sand-600">
                    Unique: <span className="text-sand-800 tnum">{stats.uniqueCount?.toLocaleString()}</span>
                  </div>
                  <div className="text-sand-600">
                    Null: <span className={`tnum ${stats.nullPercentage > 20 ? 'text-bill-600' : 'text-sand-800'}`}>
                      {stats.nullPercentage}%
                    </span>
                  </div>

                  {stats.numericStats && (
                    <>
                      <div className="text-sand-600">
                        Min: <span className="text-sand-800 tnum">{stats.numericStats.min}</span>
                      </div>
                      <div className="text-sand-600">
                        Max: <span className="text-sand-800 tnum">{stats.numericStats.max}</span>
                      </div>
                      <div className="text-sand-600">
                        Avg: <span className="text-sand-800 tnum">{stats.numericStats.avg}</span>
                      </div>
                      <div className="text-sand-600">
                        Median: <span className="text-sand-800 tnum">{stats.numericStats.median}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Top Values */}
                {stats.topValues?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[rgba(32,30,29,0.08)]">
                    <div className="text-xs text-sand-600 mb-1">Top values:</div>
                    <div className="flex flex-wrap gap-1">
                      {stats.topValues.slice(0, 3).map((v, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 bg-sand-200 rounded text-sand-700">
                          {String(v.value).substring(0, 20)}{String(v.value).length > 20 ? '...' : ''} ({v.count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {stats.distribution?.buckets?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[rgba(32,30,29,0.08)]">
                    <div className="text-xs text-sand-600 mb-2">Distribution (5 buckets):</div>
                    <div className="space-y-1">
                      {stats.distribution.buckets.map((bucket) => (
                        <div key={bucket.label} className="flex items-center gap-2 text-xs text-sand-700">
                          <span className="w-32 truncate">{bucket.label}</span>
                          <div className="flex-1 h-2 bg-sand-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-bill-500"
                              style={{
                                width: `${Math.min(100, (bucket.count / Math.max(...stats.distribution.buckets.map((b) => b.count || 0), 1)) * 100)}%`
                              }}
                            />
                          </div>
                          <span className="w-10 text-right tnum">{bucket.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Smart Suggestions */}
      {profile?.suggestions?.length > 0 && (
        <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4">
          <h3 className="font-medium text-ink mb-4">💡 Suggested Questions</h3>
          <div className="grid gap-2">
            {profile.suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onQuestionClick?.(suggestion.question)}
                className="flex items-start gap-3 p-3 bg-sand-100 hover:bg-sand-200 rounded-brand-sm text-left transition-colors group"
              >
                <div className="p-1.5 bg-bill-100 rounded">
                  <Sparkles className="w-3 h-3 text-bill-600" />
                </div>
                <div>
                  <p className="text-ink group-hover:text-bill-700 transition-colors">
                    {suggestion.question}
                  </p>
                  <p className="text-xs text-sand-600 mt-0.5">{suggestion.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {clientAIService.hasApiKey() && (
        <div className="bg-bill-100 border border-bill-200 shadow-brand-sm rounded-brand-md p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-bill-600" />
              <h3 className="font-medium text-ink">AI Insights</h3>
            </div>
            <button
              onClick={loadAIInsights}
              disabled={insightsLoading}
              className="px-3 py-1.5 bg-bill-500 hover:bg-bill-600 text-white rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {insightsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Generate Insights'
              )}
            </button>
          </div>

          {aiInsights ? (
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-sand-800 text-sm leading-relaxed">
                {aiInsights}
              </div>
            </div>
          ) : (
            <p className="text-sm text-sand-600">
              Click "Generate Insights" to get AI-powered analysis of your dataset.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DataProfile;
